import { blue, slate } from '@/lib/colours';

export const LANDING_MAX_WIDTH_PX = 1216;

export const sectionShell = {
  width: '100%',
  margin: '0 auto',
  maxWidth: `${LANDING_MAX_WIDTH_PX}px`,
  px: { xs: `${20}px`, md: `${64}px` },
};

/**
 * Scheme-aware landing tokens. The landing is a fixed-palette design (its
 * sections don't read MUI theme tokens), which made the light sections glare
 * in dark mode. These CSS custom properties carry the light values by default
 * and flip to dark equivalents under `[data-mui-color-scheme="dark"]` — the
 * sections stay hook-free server components. HomeLanding spreads this on the
 * landing root; every `var(--landing-*)` below resolves inside it.
 */
export const landingSchemeVars = {
  '--landing-surface': slate[50],
  '--landing-surface-muted': slate[100],
  '--landing-card': slate[50],
  '--landing-line': slate[200],
  '--landing-line-accent': blue[100],
  '--landing-ink': slate[900],
  '--landing-ink-body': slate[600],
  '--landing-ink-strong': slate[700],
  '--landing-ink-soft': slate[500],
  '--landing-eyebrow': blue[700],
  '--landing-media-well': blue[50],
  '[data-mui-color-scheme="dark"] &': {
    '--landing-surface': slate[900],
    '--landing-surface-muted': slate[950],
    '--landing-card': slate[900],
    '--landing-line': slate[800],
    '--landing-line-accent': `${blue[500]}33`,
    '--landing-ink': slate[50],
    '--landing-ink-body': slate[400],
    '--landing-ink-strong': slate[300],
    '--landing-ink-soft': slate[400],
    '--landing-eyebrow': blue[400],
    '--landing-media-well': `${blue[500]}14`,
  },
} as const;

export const darkSectionBg = {
  backgroundColor: slate[950],
};

export const lightSectionBg = {
  backgroundColor: 'var(--landing-surface)',
};

export const mutedSectionBg = {
  backgroundColor: 'var(--landing-surface-muted)',
};

/** Registry landing (and legacy) hero fill — not used on home `/`. */
export const heroSectionBg = {
  backgroundColor: slate[900],
  backgroundImage: `
    linear-gradient(180deg, ${slate[900]} 0%, ${slate[800]} 42%, ${slate[900]} 100%),
    linear-gradient(rgba(59, 130, 246, 0.085) 1px, transparent 1px),
    linear-gradient(90deg, rgba(59, 130, 246, 0.085) 1px, transparent 1px)
  `,
  backgroundSize: '100% 100%, 48px 48px, 48px 48px',
};

/** Registry landing accent — not used on home `/`. */
export const heroAccentGlow = {
  background: `radial-gradient(ellipse 85% 65% at 50% -28%, ${blue[500]}40, transparent 58%)`,
};

/** Left-weighted scrim for type — keeps the right side open so photo colour reads. */
export const heroMediaScrim = {
  background: `
    linear-gradient(
      105deg,
      ${slate[950]}e8 0%,
      ${slate[950]}cc 28%,
      ${slate[950]}80 48%,
      ${slate[950]}2a 68%,
      transparent 82%
    ),
    linear-gradient(
      180deg,
      ${slate[950]}55 0%,
      transparent 22%,
      transparent 78%,
      ${slate[950]}aa 100%
    )
  `,
};

/** Bottom edge fade into the next section (trust strip surface). */
export const heroBottomFade = {
  background: `linear-gradient(180deg, transparent 0%, var(--landing-surface) 100%)`,
};

export const sectionEyebrowSx = {
  fontWeight: 600,
  letterSpacing: '0.06em',
  fontSize: '0.75rem',
  color: 'var(--landing-eyebrow)',
} as const;
